import { FC, useMemo } from 'react';

import { TableNode, WarehouseEntityType } from '@/__generated__/graphql';
import { ColumnTable, Entry } from '@/components/ColumnTable';
import { TagLinks } from '@/components/ResourcePanel/ResourcePanels/TagLinks';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { useWarehouseEntity } from '@/hooks/discovery/catalog/entities';
import { formatAbsoluteLocalDate } from '@/util/date';
import { formatBytes } from '@/util/formatBytes';
import { LocationDetails } from './LocationDetails';
import { AssetDetailsParams, PanelDetails } from './types';

const createTableRows = (table: TableNode): Entry[] => {
  return [
    {
      key: 'Size',
      data: formatBytes(table.sizeInBytes ?? 0),
    },
    {
      key: 'tags',
      data: <TagLinks tags={table.tags} />,
    },
    {
      key: 'Relation',
      data: (
        <div>
          {table.qualifiedName && <div>{table.qualifiedName.toLowerCase()}</div>}
        </div>
      ),
    },
    {
      key: 'Location',
      data: (
        <LocationDetails
          fqn={table.qualifiedName}
          entityType={WarehouseEntityType.Table}
        />
      ),
    },
    {
      key: 'Last synced',
      data: table.lastSyncedAt ? (
        formatAbsoluteLocalDate(new Date(table.lastSyncedAt))
      ) : (
        <div>Unknown</div>
      ),
    },
  ];
};

export const useTableDetailsPanel = ({ assetId }: AssetDetailsParams) => {
  const { accountId } = useDbtCloudAccount();

  const tableQuery = useWarehouseEntity({
    accountId,
    filter: {
      urn: assetId,
      parent_urn: '',
      type: null,
    },
  });

  return useMemo(() => {
    const table = tableQuery.data as TableNode | undefined;
    if (!table)
      return { ...tableQuery, rows: null, description: null } satisfies PanelDetails;

    const rows = createTableRows(table);

    return {
      ...tableQuery,
      rows,
      description: table?.description,
    };
  }, [tableQuery]);
};

type TableDetailsProps = {
  assetId: string;
  compact?: boolean;
};

export const TableDetails: FC<TableDetailsProps> = ({ assetId, compact }) => {
  const tableDetails = useTableDetailsPanel({ assetId });
  return (
    <div>
      <ColumnTable
        tableEntries={tableDetails.rows}
        compact={compact}
        isLoading={tableDetails.fetching}
      />
    </div>
  );
};
